import { useLazyGetAdMetricsTimeseriesQuery } from '@/lib/features/adMetrics/adMetricsApi'
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js'
import { ChevronDown, TrendingUp } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Line } from 'react-chartjs-2'
import {
  AdPlatform,
  EntityType,
  FACEBOOK_AD_METRICS,
  GOOGLE_AD_METRICS,
  TimeGranularity,
} from 'sprites-types/api/v2'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

type AdMetricsChartProps = {
  entityId: string
  entityName: string
  entityType: EntityType
  platform: AdPlatform
  initialDateRange?: { startDate: string; endDate: string }
}

const GRANULARITY_OPTIONS: TimeGranularity[] = ['daily', 'weekly', 'monthly']

function formatMetricLabel(metric: string): string {
  const labels: Record<string, string> = {
    impressions: 'Impressions',
    clicks: 'Clicks',
    spend: 'Spend',
    cost: 'Cost',
    cpm: 'CPM',
    cpc: 'CPC',
    ctr: 'CTR (%)',
    conversions: 'Conversions',
    conversion_rate: 'Conversion Rate (%)',
    impression_share: 'Impression Share (%)',
  }
  return labels[metric] || metric
}

function formatMetricValue(value: number, metric: string): string {
  if (metric === 'spend' || metric === 'cost' || metric === 'cpm' || metric === 'cpc') {
    return `$${value.toFixed(2)}`
  }
  if (metric === 'ctr' || metric === 'conversion_rate' || metric === 'impression_share') {
    return `${value.toFixed(2)}%`
  }
  return value.toFixed(0)
}

function getDefaultDateRange(): { startDate: string; endDate: string } {
  const endDate = new Date()
  const startDate = new Date()
  startDate.setDate(startDate.getDate() - 30)

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  }
}

export function AdMetricsChart({
  entityId,
  entityName: _entityName,
  entityType,
  platform,
  initialDateRange,
}: AdMetricsChartProps) {
  const availableMetrics = platform === 'facebook' ? FACEBOOK_AD_METRICS : GOOGLE_AD_METRICS
  const [selectedMetric, setSelectedMetric] = useState<string>(availableMetrics[0])
  const [selectedGranularity, setSelectedGranularity] = useState<TimeGranularity>('daily')
  const [showMetricDropdown, setShowMetricDropdown] = useState(false)
  const [dateRange, setDateRange] = useState(initialDateRange || getDefaultDateRange())
  const [committedDateRange, setCommittedDateRange] = useState(dateRange)

  const [fetchTimeseries, { data, isLoading, error, isFetching }] =
    useLazyGetAdMetricsTimeseriesQuery()

  const validateDateRange = (start: string, end: string): string | null => {
    const startDate = new Date(start)
    const endDate = new Date(end)
    const diffDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))

    if (startDate > endDate) {
      return 'Start date must be before end date'
    }

    // Calculate max days based on granularity (max 50 data points)
    const limits = {
      daily: { days: 50, label: '50 days' },
      weekly: { days: 50 * 7, label: '50 weeks' },
      monthly: { days: 50 * 30, label: '50 months' },
    }

    const { days: maxDays, label: maxLabel } = limits[selectedGranularity]

    if (diffDays > maxDays) {
      return `Date range cannot exceed ${maxLabel} for ${selectedGranularity} view`
    }
    return null
  }

  const handleStartDateChange = (value: string) => {
    const newRange = { ...dateRange, startDate: value }
    setDateRange(newRange)
    const validationError = validateDateRange(value, newRange.endDate)
    if (!validationError) {
      setCommittedDateRange(newRange)
    }
  }

  const handleEndDateChange = (value: string) => {
    const newRange = { ...dateRange, endDate: value }
    setDateRange(newRange)
    const validationError = validateDateRange(newRange.startDate, value)
    if (!validationError) {
      setCommittedDateRange(newRange)
    }
  }

  const dateRangeError = validateDateRange(dateRange.startDate, dateRange.endDate)

  // Re-validate and re-commit when granularity changes
  useEffect(() => {
    const error = validateDateRange(dateRange.startDate, dateRange.endDate)
    if (!error) {
      setCommittedDateRange(dateRange)
    }
  }, [selectedGranularity])

  useEffect(() => {
    fetchTimeseries({
      entityId,
      entityType,
      platform,
      metric: selectedMetric,
      startDate: committedDateRange.startDate,
      endDate: committedDateRange.endDate,
      granularity: selectedGranularity,
    })
  }, [
    entityId,
    entityType,
    platform,
    selectedMetric,
    selectedGranularity,
    committedDateRange,
    fetchTimeseries,
  ])

  const chartData = useMemo(() => {
    if (!data || data.dataPoints.length === 0) {
      return null
    }

    return {
      labels: data.dataPoints.map((point) => {
        const date = new Date(point.date)
        if (selectedGranularity === 'daily') {
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        } else if (selectedGranularity === 'weekly') {
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        } else {
          return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
        }
      }),
      datasets: [
        {
          label: formatMetricLabel(selectedMetric),
          data: data.dataPoints.map((point) => point.value),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.3,
          fill: true,
        },
      ],
    }
  }, [data, selectedMetric, selectedGranularity])

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              return `${formatMetricLabel(selectedMetric)}: ${formatMetricValue(context.parsed.y, selectedMetric)}`
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: (value: number | string) => formatMetricValue(Number(value), selectedMetric),
          },
        },
      },
    }),
    [selectedMetric]
  )

  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-4 flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            <h3 className="text-base font-semibold text-gray-900">Performance Over Time</h3>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setShowMetricDropdown(!showMetricDropdown)}
                className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {formatMetricLabel(selectedMetric)}
                <ChevronDown className="h-4 w-4" />
              </button>
              {showMetricDropdown && (
                <div className="absolute right-0 z-10 mt-1 w-48 rounded-md border border-gray-200 bg-white shadow-lg">
                  {availableMetrics.map((metric) => (
                    <button
                      key={metric}
                      onClick={() => {
                        setSelectedMetric(metric)
                        setShowMetricDropdown(false)
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                        selectedMetric === metric
                          ? 'bg-blue-50 font-medium text-blue-600'
                          : 'text-gray-700'
                      }`}
                    >
                      {formatMetricLabel(metric)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex rounded-md border border-gray-300 bg-white">
              {GRANULARITY_OPTIONS.map((granularity) => (
                <button
                  key={granularity}
                  onClick={() => setSelectedGranularity(granularity)}
                  className={`px-3 py-2 text-sm font-medium capitalize first:rounded-l-md last:rounded-r-md ${
                    selectedGranularity === granularity
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {granularity}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Date Range:</span>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                max={dateRange.endDate}
                className={`rounded-md border bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 ${
                  dateRangeError
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                } [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:hover:brightness-75`}
              />
              <span className="text-sm font-medium text-gray-500">to</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => handleEndDateChange(e.target.value)}
                min={dateRange.startDate}
                max={new Date().toISOString().split('T')[0]}
                className={`rounded-md border bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 ${
                  dateRangeError
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                } [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:hover:brightness-75`}
              />
            </div>
            <span className="text-xs text-gray-500">
              (max{' '}
              {selectedGranularity === 'daily'
                ? '50 days'
                : selectedGranularity === 'weekly'
                  ? '50 weeks'
                  : '50 months'}
              )
            </span>
          </div>
          {dateRangeError && <div className="ml-24 text-xs text-red-600">{dateRangeError}</div>}
        </div>
      </div>

      <div className="h-80">
        {isLoading || isFetching ? (
          <div className="flex h-full items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
          </div>
        ) : error ? (
          <div className="flex h-full items-center justify-center">
            <div className="max-w-md text-center">
              <p className="text-sm font-medium text-red-600">Failed to load chart data</p>
              <p className="mt-1 text-xs text-gray-500">
                {error && 'data' in error && error.data
                  ? typeof error.data === 'object' && 'error' in error.data
                    ? String(error.data.error)
                    : 'status' in error && typeof error.status === 'number'
                      ? `Error ${error.status}: ${error.status === 404 ? 'Credentials not found. Please connect your account in settings.' : 'Server error'}`
                      : 'Unknown error'
                  : error && 'message' in error
                    ? String(error.message)
                    : 'Unknown error'}
              </p>
              <button
                onClick={() =>
                  fetchTimeseries({
                    entityId,
                    entityType,
                    platform,
                    metric: selectedMetric,
                    startDate: dateRange.startDate,
                    endDate: dateRange.endDate,
                    granularity: selectedGranularity,
                  })
                }
                className="mt-3 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          </div>
        ) : !chartData || chartData.labels.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-900">No data available</p>
              <p className="mt-1 text-xs text-gray-500">
                No metrics found for the selected time range
              </p>
            </div>
          </div>
        ) : (
          <Line data={chartData} options={chartOptions} />
        )}
      </div>
    </div>
  )
}
